// ── Capital letters in a natural person's name (Calem 2026-09-25) ───────────────────────────────
// Frontend mirror of normalisePersonNamePart in server/src/services/clientName.ts — keep the two in
// step. The server applies it when an individual is saved; the First / Middle / Last boxes apply it
// as they lose focus, so the correction is on screen before Save is pressed.
//   "SMITH" → "Smith" · "smith" → "Smith" · "O'NEIL" → "O'Neil" · "McDonald" stays as typed ·
//   Thai stays as typed · "j.r." → "J.R." · "III" stays as typed.
// The workspace's own word in capitals stays as typed too (Calem 2026-10-04, report 077fd777: "Also
// allow the letters SLF to be capital") — taken from the brand and the firms' labels /api/me sends
// (utils/brand.ts, Admin → Companies), the same words the server keeps; never spelt here.
import { brand, firmLabel } from './brand';

const CASED_WORD = /([\p{Lu}\p{Ll}\p{Lt}])([\p{Lu}\p{Ll}\p{Lt}\p{M}]*)/gu;
const NUMERAL_SUFFIX = /^(II|III|IV)$/;
const SMALL_LETTER = /\p{Ll}/u;

/** The words of these names written wholly in capitals — "ACME Legal" → ["ACME"]. */
export function capitalsWordsOf(names: string[]): string[] {
  const out: string[] = [];
  for (const n of names) {
    const found: string[] = String(n || '').match(CASED_WORD) || [];
    for (const w of found) if (!SMALL_LETTER.test(w) && !out.includes(w)) out.push(w);
  }
  return out;
}

/** The workspace's capitals words: those of the brand and of both firm labels. */
export const workspaceCapitalsWords = (): string[] => capitalsWordsOf([brand(), firmLabel('legal'), firmLabel('accounting')]);

export function normalisePersonNamePart(part: string, keep: string[] = workspaceCapitalsWords()): string {
  return part.replace(CASED_WORD, (w: string, head: string, rest: string) =>
    NUMERAL_SUFFIX.test(w) || keep.includes(w) ? w : head.toUpperCase() + (rest === rest.toUpperCase() ? rest.toLowerCase() : rest));
}

/** A saved name as a person's form opens it (Calem 2026-10-04, report 077fd777: "I'm only looking to
 *  correct all caps names"): only the words written wholly in capitals are corrected — "JUN" → "Jun";
 *  "McDonald", "jun" and Thai are shown as saved. Mirror of fixAllCaps in services/clientName.ts. */
export function fixAllCaps(part: string, keep: string[] = workspaceCapitalsWords()): string {
  return part.replace(CASED_WORD, (w: string) => (SMALL_LETTER.test(w) ? w : normalisePersonNamePart(w, keep)));
}
